import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, CircularProgress, Alert, TextField, InputAdornment, MenuItem, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Tooltip, IconButton, Link,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { formatDateTime } from '../../utils/dateUtils';
import { AdminPageHeader, errorMessage } from '../../components/Admin/adminUi';

const METHOD_COLORS = { POST: 'info', PUT: 'warning', PATCH: 'warning', DELETE: 'error' };

const AdminAudit = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.admin.getAudit();
      setLogs(res.data.logs || []);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load audit log'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const companies = useMemo(() => {
    const map = new Map();
    logs.forEach((l) => { if (l.companyId) map.set(String(l.companyId), l.company); });
    return [...map.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [logs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (companyFilter !== 'all' && String(l.companyId) !== companyFilter) return false;
      if (resultFilter === 'success' && !l.success) return false;
      if (resultFilter === 'failure' && l.success) return false;
      if (methodFilter !== 'all' && l.method !== methodFilter) return false;
      if (!q) return true;
      return [l.company, l.userEmail, l.userName, l.action, l.resource, l.resourceId, l.path, l.method, l.status, l.ip]
        .some((v) => v !== undefined && v !== null && String(v).toLowerCase().includes(q));
    });
  }, [logs, search, companyFilter, resultFilter, methodFilter]);

  if (loading && logs.length === 0) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  return (
    <Box>
      <AdminPageHeader
        title="Audit"
        subtitle="Most recent mutating API requests across all tenants (up to 20 per company, 200 total)"
        actions={<Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>}
      />
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Filter by company, user, action, path, status or IP"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
            <TextField select size="small" label="Company" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} sx={{ minWidth: 200 }}>
              <MenuItem value="all">All companies</MenuItem>
              {companies.map(([cid, name]) => <MenuItem key={cid} value={cid}>{name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Method" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="all">All</MenuItem>
              {['POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Result" value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failure">Failed</MenuItem>
            </TextField>
          </Stack>

          <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Path</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>{logs.length ? 'No entries match the current filters' : 'No audit entries yet'}</Typography>
                    </TableCell>
                  </TableRow>
                ) : filtered.map((l, i) => (
                  <TableRow key={l._id || l.id || i} hover>
                    <TableCell><Typography variant="body2" noWrap>{formatDateTime(l.createdAt)}</Typography></TableCell>
                    <TableCell>
                      {l.companyId
                        ? <Link component={RouterLink} to={`/admin/customers/${l.companyId}`} underline="hover">{l.company}</Link>
                        : l.company}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{l.userName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{l.userEmail}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip size="small" label={l.method} color={METHOD_COLORS[l.method] || 'default'} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                        <Typography variant="body2">{l.resource}{l.action ? ` · ${l.action}` : ''}</Typography>
                      </Box>
                      {l.resourceId && <Typography variant="caption" color="text.secondary">{l.resourceId}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={l.details ? JSON.stringify(l.details) : ''} placement="top-start">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{l.path}</Typography>
                      </Tooltip>
                      {l.ip && <Typography variant="caption" color="text.secondary">{l.ip}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={l.status ?? (l.success ? 'OK' : 'FAIL')} color={l.success ? 'success' : 'error'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Showing {filtered.length} of {logs.length} entries</Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminAudit;
